import * as React from 'react'
import { redirect } from 'react-router-dom'

import {
  PerformanceEvaluationAnswerHeaderDocument,
  PerformanceProcessAgreementCrumbDocument,
  PerformanceProcessTargetCrumbDocument,
  PerformanceReviewOverviewHeaderDocument,
  PerformanceReviewOverviewSubNavigationFragment,
  PerformanceTemplateEditPageHeaderDocument,
} from 'generated/resources/graphql'
import { keyBy } from 'lodash'
import { compact } from 'lodash'

import { Employee } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import { NavigationElement } from 'modules/core/lib/navigation/types'
import { NavigationRoutesFn, buildNavigation } from 'modules/core/lib/react-router'

import { getEvaluationType } from 'modules/performance_process/components/review/answer/helpers/getEvaluationType'
import { ReviewOverviewHeader } from 'modules/performance_process/pages/reviews/show/overview/Header'
import { EvaluationAnswerHeader } from 'modules/performance_process/pages/reviews/show/reviewsToFill/EvaluationAnswerHeader'
import { ReviewProcessSettingsHeader } from 'modules/performance_process/pages/reviews/show/settings/ReviewProcessSettingsHeader'
import { ReviewProcessTargetHeader } from 'modules/performance_process/pages/reviews/show/target/ReviewProcessTargetHeader'
import { ReviewProcessAgreementHeader } from 'modules/performance_process/pages/reviews/show/target/agreement/ReviewProcessAgreementHeader'
import { TemplateHeader } from 'modules/performance_process/pages/templates/TemplateEditPage/TemplateHeader'
import { TemplateSettingsHeader } from 'modules/performance_process/pages/templates/TemplateEditPage/TemplateSettingsHeader'

const ReviewsRootPage = React.lazy(
  () => import('modules/performance_process/pages/ReviewsRootPage')
)

const ReviewsListPage = React.lazy(() => import('../pages/reviews/ReviewsListPage'))
const ReviewOverviewPage = React.lazy(() => import('../pages/reviews/show/overview'))
const ReviewOverviewDraftPage = React.lazy(() => import('../pages/reviews/show/overview/Draft'))
const ReviewOverviewRedirectPage = React.lazy(
  () => import('../pages/reviews/show/overview/Redirect')
)
const ReviewOverviewInsightsPage = React.lazy(
  () => import('../pages/reviews/show/overview/Insights')
)
const ReviewOverviewPageAnswers = React.lazy(() => import('../pages/reviews/show/overview/Answers'))
const ReviewOverviewHeatmapPage = React.lazy(() => import('../pages/reviews/show/overview/Heatmap'))
const ReviewOverviewTeamInsightsPage = React.lazy(
  () => import('../pages/reviews/show/overview/TeamInsights')
)
const ReviewOverviewPersonalResultsPage = React.lazy(
  () => import('../pages/reviews/show/overview/PersonalResults')
)
const EvaluationAnswerPage = React.lazy(
  () => import('../pages/reviews/show/reviewsToFill/EvaluationAnswerPage')
)
const ReviewSettingsPage = React.lazy(
  () => import('../pages/reviews/show/settings/ReviewSettingsPage')
)
const Target = React.lazy(() => import('../pages/reviews/show/target'))
const Agreement = React.lazy(() => import('../pages/reviews/show/target/agreement'))

const TemplatesListPage = React.lazy(() => import('../pages/templates/TemplatesListPage'))
const TemplateEditPage = React.lazy(() => import('../pages/templates/TemplateEditPage'))
const TemplateSettingsPage = React.lazy(
  () => import('../pages/templates/TemplateEditPage/TemplateSettingsPage')
)

type Navigation = Parameters<NavigationRoutesFn>[0]

const reviewOverviewSubNavigation = ({
  navigation,
  reviewProcess,
  currentEmployee,
}: {
  navigation: Navigation
  reviewProcess: PerformanceReviewOverviewSubNavigationFragment
  currentEmployee: Employee
}): NavigationElement[] => {
  if (reviewProcess.status === 'draft') return []

  const canOnlySeePersonalResults =
    !reviewProcess._policies?.fullVisibility.can &&
    (currentEmployee ? reviewProcess?.reportsTargets?.totalCount ?? 0 : 0) === 0

  if (canOnlySeePersonalResults) return []

  const isCurrentAccessTarget = Boolean(reviewProcess.currentAccessTarget?.nodes[0]?.access)

  return compact([
    {
      title: i18n.t('performance_process.overview.results.insights_tab'),
      href: navigation.performanceProcess.reviews.review.overview.insights.fullPath({
        reviewId: reviewProcess.id,
      }),
    },
    {
      title: i18n.t('performance_process.overview.results.answers_tab'),
      href: navigation.performanceProcess.reviews.review.overview.answers.fullPath({
        reviewId: reviewProcess.id,
      }),
    },
    {
      title: i18n.t('performance_process.overview.results.heatmap_tab'),
      href: navigation.performanceProcess.reviews.review.overview.heatmap.fullPath({
        reviewId: reviewProcess.id,
      }),
    },
    isCurrentAccessTarget && {
      title: i18n.t('performance_process.overview.results.personal_results_title'),
      href: navigation.performanceProcess.reviews.review.overview.personalResults.fullPath({
        reviewId: reviewProcess.id,
      }),
    },
    {
      title: i18n.t('performance_process.overview.results.team_insights_title'),
      href: navigation.performanceProcess.reviews.review.overview.teamInsights.fullPath({
        reviewId: reviewProcess.id,
      }),
    },
  ])
}

export const performanceProcessRoutes = buildNavigation((navigation) => {
  return [
    {
      path: navigation.performanceProcess.reviews.path.replace(
        `${navigation.performanceProcess.path}/`,
        ''
      ),
      element: <ReviewsRootPage />,
      handle: {
        meta: () => {
          return {
            title: i18n.t('performance_process.reviews.reviews'),
            description: i18n.t('performance_process.reviews.performance_index_description'),
            illustration: 'performance_management',
          }
        },
      },
      children: [
        {
          index: true,
          element: <ReviewsListPage />,
        },
        {
          path: navigation.performanceProcess.reviews.review.path,
          handle: {
            resource: true,
            meta: ({ watchQuery, params, session }) => {
              if (!params['reviewId']) return { title: '', description: '' }

              return watchQuery({
                query: PerformanceReviewOverviewHeaderDocument,
                variables: {
                  id: Number(params['reviewId']),
                  companyId: session.company.id,
                  currentAccessId: session.access.id,
                  directManagedByCurrentEmployeeFilter: session.employee
                    ? {
                        managerEmployeeId: session.employee.id,
                        onlyDirectReports: true,
                      }
                    : undefined,
                  managedByCurrentEmployeeFilter: session.employee
                    ? {
                        managerEmployeeId: session.employee.id,
                        onlyDirectReports: false,
                      }
                    : undefined,
                },
              }).map(({ data }) => {
                const reviewProcess = data?.performance?.reviewProcess
                const features = keyBy(
                  data?.features?.featuresConnection?.edges,
                  (edge) => edge.node.name
                )
                const hasCyclesFeatureEnabled = !!features['dev_connect_goals_and_reviews_v2']
                const isLegacyJobCatalogEnabled = !!features['legacy_job_catalog']
                const isJobCatalogTreeEnabled = !isLegacyJobCatalogEnabled

                return {
                  title: reviewProcess?.name || i18n.t('performance_process.reviews.untitled'),
                  description:
                    reviewProcess?.description ||
                    i18n.t('performance_process.reviews.no_description'),
                  headerComponent: reviewProcess && (
                    <ReviewOverviewHeader
                      reviewProcess={reviewProcess}
                      hasCyclesFeatureEnabled={hasCyclesFeatureEnabled}
                      isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
                    />
                  ),
                  subNavigation: reviewProcess
                    ? reviewOverviewSubNavigation({
                        navigation,
                        reviewProcess,
                        currentEmployee: session.employee,
                      })
                    : [],
                }
              })
            },
          },
          children: [
            {
              index: true,
              loader: ({ params, request }) => {
                const url = new URL(request.url)
                return redirect(
                  navigation.performanceProcess.reviews.review.overview.fullPath({
                    reviewId: Number(params.reviewId),
                  }) + url.search
                )
              },
            },
            {
              path: navigation.performanceProcess.reviews.review.overview.path,
              element: <ReviewOverviewPage />,
              children: [
                {
                  index: true,
                  element: <ReviewOverviewRedirectPage />,
                },
                {
                  path: navigation.performanceProcess.reviews.review.overview.draft.path,
                  element: <ReviewOverviewDraftPage />,
                },
                {
                  path: navigation.performanceProcess.reviews.review.overview.insights.path,
                  element: <ReviewOverviewInsightsPage />,
                },
                {
                  path: navigation.performanceProcess.reviews.review.overview.answers.path,
                  element: <ReviewOverviewPageAnswers />,
                },
                {
                  path: navigation.performanceProcess.reviews.review.overview.heatmap.path,
                  element: <ReviewOverviewHeatmapPage />,
                },
                {
                  path: navigation.performanceProcess.reviews.review.overview.personalResults.path,
                  element: <ReviewOverviewPersonalResultsPage />,
                },
                {
                  path: navigation.performanceProcess.reviews.review.overview.teamInsights.path,
                  element: <ReviewOverviewTeamInsightsPage />,
                },
              ],
            },
            {
              path: navigation.performanceProcess.reviews.review.myResultsEvaluation.path,
              handle: {
                resource: true,
                meta: ({ params, watchQuery }) => {
                  if (!params['evaluationId']) {
                    return {
                      title: i18n.t('performance_process.overview.results.personal_results_title'),
                    }
                  }
                  return watchQuery({
                    query: PerformanceEvaluationAnswerHeaderDocument,
                    variables: {
                      id: Number(params['evaluationId']),
                    },
                  }).map((result) => {
                    const evaluation = result?.data?.performance?.reviewEvaluation
                    return {
                      title: i18n.t('performance_process.overview.results.personal_results_title'),
                      headerComponent: evaluation && (
                        <EvaluationAnswerHeader evaluation={evaluation} />
                      ),
                    }
                  })
                },
              },
              element: <EvaluationAnswerPage />,
            },
            {
              path: navigation.performanceProcess.reviews.review.myResultsAgreement.path,
              handle: {
                resource: true,
                meta: ({ params, watchQuery, session }) => {
                  if (!params['agreementId']) {
                    return {
                      title: i18n.t('performance_process.agreements.page.title'),
                    }
                  }

                  return watchQuery({
                    query: PerformanceProcessAgreementCrumbDocument,
                    variables: {
                      id: params['agreementId'],
                      companyId: session.company.id,
                    },
                  }).map((result) => {
                    const agreement = result?.data?.performance?.agreement

                    const isAgreementDocumentEnabled =
                      result?.data?.features?.featuresConnection?.nodes?.find(
                        (feature) => feature.name === 'dev_performance_agreement_document'
                      )?.enabled ?? false

                    return {
                      title: i18n.t('performance_process.agreements.page.title'),
                      description: i18n.t('performance_process.agreements.page.description'),
                      headerComponent: agreement && (
                        <ReviewProcessAgreementHeader
                          agreement={agreement}
                          isAgreementDocumentEnabled={isAgreementDocumentEnabled}
                        />
                      ),
                    }
                  })
                },
              },
              element: <Agreement />,
            },
            {
              path: navigation.performanceProcess.reviews.review.settings.path,
              handle: {
                resource: true,
                meta: () => ({
                  title: i18n.t('performance_process.reviews.edit'),
                  headerComponent: <ReviewProcessSettingsHeader />,
                }),
              },
              element: <ReviewSettingsPage />,
            },
            {
              path: navigation.performanceProcess.reviews.review.target.path,
              handle: {
                resource: true,
                meta: ({ params, watchQuery }) => {
                  if (!params['targetId']) return { title: '', description: '' }
                  return watchQuery({
                    query: PerformanceProcessTargetCrumbDocument,
                    variables: {
                      targetId: params['targetId']?.toString() ?? '',
                    },
                  }).map((result) => {
                    const targetAccess = result?.data?.performance?.reviewProcessTarget?.access

                    if (!targetAccess)
                      return {
                        title: '',
                      }

                    return {
                      title: targetAccess.fullName ?? '',
                      description: targetAccess.employee?.referenceContract?.jobTitle ?? '',
                      headerComponent: <ReviewProcessTargetHeader target={targetAccess} />,
                    }
                  })
                },
              },
              children: [
                {
                  index: true,
                  element: <Target />,
                },
                {
                  path: navigation.performanceProcess.reviews.review.target.evaluation.path,
                  handle: {
                    resource: true,
                    meta: ({ params, watchQuery }) => {
                      if (!params['evaluationId']) return { title: '' }
                      return watchQuery({
                        query: PerformanceEvaluationAnswerHeaderDocument,
                        variables: {
                          id: Number(params['evaluationId']),
                        },
                      }).map((result) => {
                        const evaluation = result?.data?.performance?.reviewEvaluation
                        const evaluationTypeData = getEvaluationType(
                          evaluation?.evaluationType || ''
                        )

                        return {
                          title: evaluationTypeData.title,
                          headerComponent: evaluation && (
                            <EvaluationAnswerHeader evaluation={evaluation} />
                          ),
                        }
                      })
                    },
                  },
                  element: <EvaluationAnswerPage />,
                },
                {
                  path: navigation.performanceProcess.reviews.review.target.agreement.path,
                  handle: {
                    resource: true,
                    meta: ({ params, watchQuery, session }) => {
                      if (!params['agreementId']) {
                        return {
                          title: i18n.t('performance_process.agreements.page.title'),
                        }
                      }

                      return watchQuery({
                        query: PerformanceProcessAgreementCrumbDocument,
                        variables: {
                          id: params['agreementId'],
                          companyId: session.company.id,
                        },
                      }).map((result) => {
                        const agreement = result?.data?.performance?.agreement

                        const isAgreementDocumentEnabled =
                          result?.data?.features?.featuresConnection?.nodes?.find(
                            (feature) => feature.name === 'dev_performance_agreement_document'
                          )?.enabled ?? false

                        return {
                          title: i18n.t('performance_process.agreements.page.title'),
                          description: i18n.t('performance_process.agreements.page.description'),
                          headerComponent: agreement && (
                            <ReviewProcessAgreementHeader
                              agreement={agreement}
                              isAgreementDocumentEnabled={isAgreementDocumentEnabled}
                            />
                          ),
                        }
                      })
                    },
                  },
                  element: <Agreement />,
                },
              ],
            },
            {
              path: navigation.performanceProcess.reviews.review.completedReviews.path,
              children: [
                {
                  index: true,
                  handle: {
                    resource: true,
                    meta: ({ params, watchQuery }) => {
                      if (!params['evaluationId']) return { title: '' }
                      return watchQuery({
                        query: PerformanceEvaluationAnswerHeaderDocument,
                        variables: {
                          id: Number(params['evaluationId']),
                        },
                      }).map((result) => {
                        const evaluation = result?.data?.performance?.reviewEvaluation
                        const { title } = getEvaluationType(evaluation?.evaluationType || '')

                        return {
                          title: title,
                          headerComponent: evaluation && (
                            <EvaluationAnswerHeader evaluation={evaluation} />
                          ),
                        }
                      })
                    },
                  },
                  element: <EvaluationAnswerPage />,
                },
              ],
            },
          ],
        },
        {
          path: navigation.performanceProcess.reviews.templates.path,
          handle: {
            resource: true,
            meta: () => {
              return {
                title: i18n.t('performance_process.reviews.templates.title'),
                illustration: 'template',
              }
            },
          },
          children: [
            {
              index: true,
              element: <TemplatesListPage />,
            },
            {
              path: navigation.performanceProcess.reviews.templates.template.path,
              handle: {
                resource: true,
                meta: ({ watchQuery, params }) => {
                  if (!params['templateId']) return { title: '', description: '' }

                  return watchQuery({
                    query: PerformanceTemplateEditPageHeaderDocument,
                    variables: {
                      templateId: Number(params['templateId']),
                    },
                  }).map(
                    ({
                      data: {
                        performance: { reviewProcessCustomTemplate },
                      },
                    }) => {
                      return {
                        title: reviewProcessCustomTemplate?.name || '',
                        description: reviewProcessCustomTemplate?.description || '',
                        headerComponent: reviewProcessCustomTemplate && (
                          <TemplateHeader template={reviewProcessCustomTemplate} />
                        ),
                      }
                    }
                  )
                },
              },
              children: [
                {
                  index: true,
                  element: <TemplateEditPage />,
                },
                {
                  path: navigation.performanceProcess.reviews.templates.template.settings.path,
                  handle: {
                    resource: true,
                    meta: () => ({
                      title: i18n.t('performance_process.reviews.templates.edit.title'),
                      headerComponent: <TemplateSettingsHeader />,
                    }),
                  },
                  element: <TemplateSettingsPage />,
                },
              ],
            },
          ],
        },
      ],
    },
  ]
})
